import { EmptyStateView } from '@/components/EmptyStateView/EmptyStateView';
import { ScreenHeader } from '@/components/ScreenHeader';
import { TabScreenContainer } from '@/components/TabScreenContainer';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useSpeciesSelectionStore } from '@/store/speciesSelection/speciesSelection.store';
import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter, useSegments } from 'expo-router';
import { ScrollView, SizableText, YStack, useTheme } from 'tamagui';
import { SpeciesSearchBackAction } from '../components/SpeciesSearchBackAction';
import { useSpeciesAiMatch } from '../hooks/useSpeciesAiMatch';
import {
  navigateBackToSpeciesForm,
  navigateBackToSpeciesSearch,
} from '../speciesSearch.navigation';
import { getFileNameFromUri } from '../speciesSearch.utils';
import { SpeciesAiPreview } from './SpeciesAiPreview';
import { SpeciesAiResults } from './SpeciesAiResults';

export const SpeciesAiSearchScreen = (): React.ReactElement => {
  const { t } = useAppTranslation();
  const { isSmallScreen } = useBreakpoint();
  const router = useRouter();
  const { setSelection } = useSpeciesSelectionStore();
  const segments = useSegments() as string[];
  const { fileUri, fileType, fileName, reportId } = useLocalSearchParams<{
    fileUri?: string;
    fileType?: string;
    fileName?: string;
    reportId?: string;
  }>();
  const resolvedFileUri = typeof fileUri === 'string' ? fileUri : undefined;
  const resolvedFileType = typeof fileType === 'string' ? fileType : undefined;
  const resolvedFileName = typeof fileName === 'string' ? fileName : undefined;
  const theme = useTheme();
  const brandColor = theme.brand?.val ?? theme.color.val;
  const showPreview = Boolean(resolvedFileUri);

  const fileLabel = resolvedFileName ?? getFileNameFromUri(resolvedFileUri);
  const { data: aiResult, isLoading } = useSpeciesAiMatch({
    fileUri: resolvedFileUri,
    fileType: resolvedFileType,
    fileName: resolvedFileName,
  });

  const handleSelect = (scientificName: string): void => {
    setSelection({
      value: scientificName,
      label: scientificName,
    });
    navigateBackToSpeciesForm({
      router,
      segments,
      reportId,
    });
  };

  const handleTrySearch = (): void => {
    navigateBackToSpeciesSearch(router);
  };

  return (
    <TabScreenContainer>
      {!isSmallScreen && (
        <ScreenHeader
          title={t('speciesSearch.ai.title')}
          actions={<SpeciesSearchBackAction />}
        />
      )}
      {showPreview ? (
        <ScrollView showsVerticalScrollIndicator={false}>
          <YStack alignItems="center" gap="$6" padding="$4" width="100%">
            <SpeciesAiPreview
              fileUri={resolvedFileUri}
              fileType={resolvedFileType}
              fileLabel={fileLabel}
              isLoading={isLoading}
            />
            {isLoading || !aiResult ? (
              <YStack alignItems="center" gap="$2">
                <Ionicons name="sparkles" size={24} color={brandColor} />
                <SizableText size="$3" fontFamily="$bold">
                  {t('speciesSearch.ai.processingTitle')}
                </SizableText>
                <SizableText size="$1" color="$secondary75" textAlign="center">
                  {t('speciesSearch.ai.processingDescription')}
                </SizableText>
                {fileLabel ? (
                  <SizableText size="$1" color="$secondary75">
                    {fileLabel}
                  </SizableText>
                ) : null}
              </YStack>
            ) : (
              <SpeciesAiResults
                bestMatch={aiResult.bestMatch}
                otherMatches={aiResult.otherMatches}
                onSelect={handleSelect}
                onTrySearch={handleTrySearch}
              />
            )}
          </YStack>
        </ScrollView>
      ) : (
        <YStack flex={1} padding="$4">
          <EmptyStateView
            title={t('speciesSearch.ai.emptyTitle')}
            description={t('speciesSearch.ai.emptyDescription')}
          />
        </YStack>
      )}
    </TabScreenContainer>
  );
};
